// GTFS static feed for the scheduled shuttle, published at /gtfs/riverside.zip
// for journey planners (Transitland, Rome2Rio). Pure data + text builders;
// scripts/prerender.js zips the output at build time.
//
// Modelled as the four physical buses a day, not per ticket product. Times
// confirmed with the operator 22 Oct 2026; departures must match
// busScheduleMeta.js and routes.js (enforced in gtfs.test.js).
// Pickup/drop-off flags follow what the booking catalog sells at each stop.
// Fares are deliberately omitted: prices live in the database and an
// admin edit would leave a copy here stale.

import { SITE_ORIGIN } from './siteMeta';

export const GTFS_PATH = '/gtfs/riverside.zip';

const AGENCY = {
  agency_id: 'riverside',
  agency_name: 'Riverside Shuttle',
  agency_url: SITE_ORIGIN,
  agency_timezone: 'Africa/Nairobi',
  agency_lang: 'en',
  agency_phone: '+254722328595',
  agency_fare_url: `${SITE_ORIGIN}/bus`,
};

export const GTFS_STOPS = [
  {
    stop_id: 'NBO_PARKSIDE',
    stop_name: 'Parkside Hotel, Nairobi',
    stop_desc: 'Riverside main station in Nairobi. Hotel pickup in central Nairobi is also included.',
    stop_lat: -1.28058,
    stop_lon: 36.81891,
  },
  {
    stop_id: 'NBO_JKIA',
    stop_name: 'Paul Cafe, JKIA Terminal 1 Arrivals',
    stop_desc: 'Outside Terminal 1 arrivals at Jomo Kenyatta International Airport. Be there 20 minutes before departure.',
    stop_lat: -1.333731,
    stop_lon: 36.927109,
  },
  {
    stop_id: 'NAMANGA',
    stop_name: 'Namanga Border Post',
    stop_desc: 'Kenya–Tanzania border. Passengers clear exit and entry immigration on foot.',
    stop_lat: -2.543,
    stop_lon: 36.789,
  },
  {
    stop_id: 'ARU_KABILA',
    stop_name: 'Kabila Junction, Arusha',
    stop_desc: 'Riverside main stop in Arusha. Hotel pickup in central Arusha is also included.',
    stop_lat: -3.376529,
    stop_lon: 36.700688,
  },
  {
    stop_id: 'MOS_YMCA',
    stop_name: 'YMCA, Moshi',
    stop_desc: 'Riverside main stop in Moshi. Hotel pickup in Moshi is also included.',
    stop_lat: -3.334883,
    stop_lon: 37.340382,
  },
];

const ROUTE = {
  route_id: 'NBO-ARU-MOS',
  agency_id: AGENCY.agency_id,
  route_short_name: 'Riverside',
  route_long_name: 'Nairobi – JKIA – Arusha – Moshi Shuttle',
  route_desc: 'Daily shuttle bus between Kenya and Tanzania via the Namanga border, with hotel pickup in Nairobi, Arusha and Moshi.',
  route_type: 3,
  route_url: `${SITE_ORIGIN}/bus`,
  route_color: 'A44432',
  route_text_color: 'FFFFFF',
};

// pickup/drop: true = allowed. arr/dep 'HH:MM'; dep defaults to arr.
export const GTFS_TRIPS = [
  {
    trip_id: 'SB_AM',
    headsign: 'Arusha & Moshi',
    direction_id: 0,
    stops: [
      { stop_id: 'NBO_PARKSIDE', arr: '08:00', pickup: true, drop: false },
      { stop_id: 'NBO_JKIA', arr: '08:30', pickup: true, drop: false },
      { stop_id: 'NAMANGA', arr: '11:00', pickup: true, drop: true },
      { stop_id: 'ARU_KABILA', arr: '13:40', dep: '14:00', pickup: true, drop: true },
      { stop_id: 'MOS_YMCA', arr: '16:30', pickup: false, drop: true },
    ],
  },
  {
    trip_id: 'SB_PM',
    headsign: 'Arusha',
    direction_id: 0,
    stops: [
      { stop_id: 'NBO_PARKSIDE', arr: '14:00', pickup: true, drop: false },
      { stop_id: 'NBO_JKIA', arr: '14:30', pickup: true, drop: false },
      { stop_id: 'NAMANGA', arr: '17:00', pickup: false, drop: true },
      { stop_id: 'ARU_KABILA', arr: '19:30', pickup: false, drop: true },
    ],
  },
  {
    trip_id: 'NB_AM',
    headsign: 'Nairobi',
    direction_id: 1,
    stops: [
      { stop_id: 'MOS_YMCA', arr: '06:00', pickup: true, drop: false },
      { stop_id: 'ARU_KABILA', arr: '07:30', dep: '08:00', pickup: true, drop: true },
      { stop_id: 'NAMANGA', arr: '11:00', pickup: true, drop: true },
      { stop_id: 'NBO_JKIA', arr: '14:30', pickup: false, drop: true },
      { stop_id: 'NBO_PARKSIDE', arr: '15:00', pickup: false, drop: true },
    ],
  },
  {
    trip_id: 'NB_PM',
    headsign: 'Nairobi',
    direction_id: 1,
    stops: [
      { stop_id: 'ARU_KABILA', arr: '14:00', pickup: true, drop: false },
      { stop_id: 'NBO_JKIA', arr: '19:30', pickup: false, drop: true },
      { stop_id: 'NBO_PARKSIDE', arr: '20:00', pickup: false, drop: true },
    ],
  },
];

const SERVICE_ID = 'DAILY';

function csvCell(value) {
  const s = value === undefined || value === null ? '' : String(value);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

function toCsv(rows) {
  const header = Object.keys(rows[0]);
  const lines = [header.join(',')].concat(rows.map((r) => header.map((k) => csvCell(r[k])).join(',')));
  return `${lines.join('\n')}\n`;
}

function gtfsDate(date) {
  return date.toISOString().slice(0, 10).replace(/-/g, '');
}

const hhmmss = (t) => `${t}:00`;

/**
 * GTFS text files as { 'stops.txt': '...', ... }. The service window rolls
 * forward a year from `today` so each deploy republishes a current feed.
 */
export function buildGtfsFiles(today = new Date()) {
  const start = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate()));
  const end = new Date(start);
  end.setUTCFullYear(end.getUTCFullYear() + 1);

  const stopTimes = GTFS_TRIPS.flatMap((trip) =>
    trip.stops.map((s, i) => ({
      trip_id: trip.trip_id,
      arrival_time: hhmmss(s.arr),
      departure_time: hhmmss(s.dep || s.arr),
      stop_id: s.stop_id,
      stop_sequence: i + 1,
      pickup_type: s.pickup ? 0 : 1,
      drop_off_type: s.drop ? 0 : 1,
      timepoint: 1,
    }))
  );

  return {
    'agency.txt': toCsv([AGENCY]),
    'stops.txt': toCsv(GTFS_STOPS),
    'routes.txt': toCsv([ROUTE]),
    'trips.txt': toCsv(
      GTFS_TRIPS.map((t) => ({
        route_id: ROUTE.route_id,
        service_id: SERVICE_ID,
        trip_id: t.trip_id,
        trip_headsign: t.headsign,
        direction_id: t.direction_id,
      }))
    ),
    'stop_times.txt': toCsv(stopTimes),
    'calendar.txt': toCsv([
      {
        service_id: SERVICE_ID,
        monday: 1,
        tuesday: 1,
        wednesday: 1,
        thursday: 1,
        friday: 1,
        saturday: 1,
        sunday: 1,
        start_date: gtfsDate(start),
        end_date: gtfsDate(end),
      },
    ]),
    'feed_info.txt': toCsv([
      {
        feed_publisher_name: AGENCY.agency_name,
        feed_publisher_url: SITE_ORIGIN,
        feed_lang: 'en',
        feed_start_date: gtfsDate(start),
        feed_end_date: gtfsDate(end),
        feed_version: gtfsDate(start),
        feed_contact_url: `${SITE_ORIGIN}/offices`,
      },
    ]),
  };
}
